/**
 * RackCapacitySimulator — Drag a rack's capacity slider on the drill-down
 * panel to preview OK / WARN / DANGER states against the current live stock.
 *
 * Live stock = rack.effectiveCapacity - rack.availableHead (from
 * warehouses/drilldown), OR falls back to `rack.currentStock` if the
 * caller already computed it. Slider goes from currentStock (min meaningful
 * cap) to 3x the current cap. Statuses:
 *   fill < 75% → OK
 *   75%-90%    → WARN
 *   > 90%      → DANGER
 *
 * "Save New Capacity" PATCHes /mtx/warehouses/{id} with rackCapacity.
 */
import { useMemo, useState } from 'react';
import { Sliders, Save, RotateCcw, Loader2, TriangleAlert, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const COLORS = { ok: '#10B981', warn: '#F59E0B', danger: '#EF4444', muted: '#94A3B8' };

function evalStatus(fillPct) {
  if (fillPct > 90) return { key: 'DANGER', color: COLORS.danger, label: 'Danger — over-utilised' };
  if (fillPct >= 75) return { key: 'WARN',  color: COLORS.warn,   label: 'Watch — nearing limit' };
  return { key: 'OK', color: COLORS.ok, label: 'Safe headroom' };
}

export default function RackCapacitySimulator({ projectId, rack, currentStock, onSaved }) {
  const stock = Number(currentStock ?? rack?.currentStock ?? 0);
  const savedCap = Number(rack?.rackCapacity ?? rack?.effectiveCapacity ?? Math.max(stock, 1));
  const unit = rack?.capacityUnit || 'units';
  const minSim = Math.max(1, Math.ceil(stock * 0.5));                     // never below half of stock
  const maxSim = Math.max(savedCap * 3, Math.ceil(stock * 2), 100);       // at least 3x saved / 2x stock / 100
  const [simCap, setSimCap] = useState(savedCap);
  const [saving, setSaving] = useState(false);

  const fillPct = useMemo(() => {
    if (simCap <= 0) return 100;
    return Math.min(999, (stock / simCap) * 100);
  }, [simCap, stock]);
  const status = evalStatus(fillPct);
  const dirty = Math.abs(simCap - savedCap) > 0.5;

  const reset = () => setSimCap(savedCap);

  const save = async () => {
    if (!rack?.id) { toast.error('Rack id missing — cannot save'); return; }
    setSaving(true);
    try {
      await api.patch(`/v1/projects/${projectId}/mtx/warehouses/${rack.id}`, {
        rackCapacity: Number(simCap),
        capacityUnit: unit,
      });
      toast.success(`Capacity updated to ${Number(simCap).toFixed(0)} ${unit}`);
      if (onSaved) onSaved({ ...rack, rackCapacity: Number(simCap) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save capacity');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border p-2.5 bg-white dark:bg-[#0a0f1e]"
         style={{ borderColor: status.color + '55' }}
         data-testid={`rack-cap-sim-${rack?.id || rack?.location}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sliders className="w-3 h-3" style={{ color: status.color }} />
          <span className="text-[10px] font-bold text-slate-700 dark:text-slate-200">Capacity What-If</span>
        </div>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-mono"
              style={{ background: status.color + '22', color: status.color }}
              data-testid={`rack-cap-sim-status-${rack?.id || rack?.location}`}>
          {status.key} · {fillPct.toFixed(0)}%
        </span>
      </div>

      {/* Slider */}
      <input
        type="range"
        min={minSim}
        max={maxSim}
        step={Math.max(1, Math.round((maxSim - minSim) / 200))}
        value={simCap}
        onChange={(e) => setSimCap(Number(e.target.value))}
        className="w-full h-1.5 accent-current"
        style={{ accentColor: status.color }}
        data-testid={`rack-cap-sim-slider-${rack?.id || rack?.location}`}
      />
      <div className="flex items-center justify-between text-[9px] font-mono text-slate-500 dark:text-slate-400 mt-0.5">
        <span>{minSim} {unit}</span>
        <span className="text-slate-700 dark:text-slate-200 font-bold">
          Sim capacity: {Number(simCap).toFixed(0)} {unit}
        </span>
        <span>{maxSim} {unit}</span>
      </div>

      {/* Fill bar preview */}
      <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
        <div className="h-full transition-all"
             style={{ width: `${Math.min(100, fillPct)}%`, background: status.color }} />
      </div>

      {/* Facts */}
      <div className="grid grid-cols-3 gap-1.5 mt-2 text-[9.5px]">
        <div className="rounded bg-slate-100 dark:bg-slate-800/40 p-1.5">
          <div className="text-[8.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400">Live stock</div>
          <div className="font-mono font-bold text-slate-700 dark:text-slate-200">{stock.toFixed(0)} {unit}</div>
        </div>
        <div className="rounded bg-slate-100 dark:bg-slate-800/40 p-1.5">
          <div className="text-[8.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400">Saved cap</div>
          <div className="font-mono font-bold text-slate-700 dark:text-slate-200">{savedCap.toFixed(0)} {unit}</div>
        </div>
        <div className="rounded p-1.5" style={{ background: status.color + '15' }}>
          <div className="text-[8.5px] uppercase tracking-wider" style={{ color: status.color }}>Sim status</div>
          <div className="font-mono font-bold flex items-center gap-0.5" style={{ color: status.color }}>
            {status.key === 'OK'
              ? <CheckCircle2 className="w-2.5 h-2.5" />
              : <TriangleAlert className="w-2.5 h-2.5" />}
            {status.label.split(' — ')[0]}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-1.5 mt-2">
        <button
          onClick={reset}
          disabled={!dirty || saving}
          className="text-[9.5px] px-2 py-1 rounded border flex items-center gap-1 disabled:opacity-40"
          style={{ borderColor: '#CBD5E1', color: '#64748B' }}
          data-testid={`rack-cap-sim-reset-${rack?.id || rack?.location}`}
        >
          <RotateCcw className="w-2.5 h-2.5" /> Reset
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="text-[9.5px] px-2 py-1 rounded font-bold flex items-center gap-1 disabled:opacity-40"
          style={{ background: status.color, color: '#fff' }}
          data-testid={`rack-cap-sim-save-${rack?.id || rack?.location}`}
        >
          {saving
            ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
            : <Save className="w-2.5 h-2.5" />}
          Save capacity
        </button>
      </div>
    </div>
  );
}
